import { useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  BoxList,
  Button,
  InfiniteScroll,
  Loading,
} from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { WorkSpaceNode } from 'modules/space_control/hooks/useWorkSpaces'

import WorkSpaceItem from './WorkSpaceItem'

interface Props {
  workAreaId: string
  locationId: string
  selectedWorkSpace?: WorkSpaceNode | null
  hoveredWorkSpaceId?: string | null
  onWorkSpaceSelect?: (workSpace: WorkSpaceNode | null) => void
  canEditSpace: boolean
  canDeleteSpace: boolean
  workSpaces: WorkSpaceNode[]
  canAddSpace: boolean
  onDeleteSuccess: () => void
  controls: {
    loading: boolean
    fetchMore: () => void
    hasMore: boolean
  }
}

const LocationWorkSpaces = ({
  workAreaId,
  locationId,
  selectedWorkSpace,
  hoveredWorkSpaceId,
  onWorkSpaceSelect,
  canEditSpace,
  canDeleteSpace,
  workSpaces,
  canAddSpace,
  onDeleteSuccess,
  controls,
}: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const infiniteScrollRef = useRef<HTMLDivElement>(null)
  const addSpaceClick = () => {
    track(trackingEvents.spaceControl.addSpace, {})
    navigate(
      navigation.settings.locations.locationsId.workAreas.workSpaces.addWorkSpace.fullPath({
        id: locationId,
        workAreaId: workAreaId,
      })
    )
  }

  const handleWorkSpaceSelect = (workSpace: WorkSpaceNode) => {
    if (onWorkSpaceSelect) {
      onWorkSpaceSelect(selectedWorkSpace?.id === workSpace.id ? null : workSpace)
    }
  }

  if (workSpaces.length === 0) {
    return (
      <Box width="s400">
        <OneEmptyState
          title={i18n.t('settings.locations.location.work_spaces.empty_message')}
          description=""
          emoji={'🏢'}
          actions={
            canAddSpace
              ? [
                  {
                    label: i18n.t('settings.locations.location.work_areas.add_space'),
                    onClick: addSpaceClick,
                    type: 'default',
                    variant: 'outline',
                  },
                ]
              : []
          }
        />
      </Box>
    )
  }

  return (
    <Box gap="s12">
      {canAddSpace && (
        <Button onClick={addSpaceClick}>
          {i18n.t('settings.locations.location.work_areas.add_space')}
        </Button>
      )}
      <BoxList>
        <Box
          gap="s12"
          ref={infiniteScrollRef}
          style={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)' }}
        >
          <InfiniteScroll
            loadMore={controls.fetchMore}
            hasMore={controls.hasMore}
            loader={
              <Box paddingTop="s12">
                <Loading />
              </Box>
            }
            initialLoad={false}
            ref={null}
            scrollContainerRef={infiniteScrollRef}
          >
            {workSpaces.map((workSpace: WorkSpaceNode) => (
              <WorkSpaceItem
                key={`${workSpace.id}${workSpace.name}`}
                workSpace={workSpace}
                locationId={locationId}
                workAreaId={workAreaId}
                isHighlighted={
                  selectedWorkSpace?.id === workSpace.id || hoveredWorkSpaceId === workSpace.id
                }
                onSelect={handleWorkSpaceSelect}
                canEditSpace={canEditSpace}
                canDeleteSpace={canDeleteSpace}
                onDeleteSuccess={onDeleteSuccess}
              />
            ))}
          </InfiniteScroll>
        </Box>
      </BoxList>
    </Box>
  )
}

export default LocationWorkSpaces
